import CustodianBasicDetailsEditForm from '../../AddLocation/Components/CustodianBasicDetails';
import CustodianContactsDetails from './CustodianContactDetails';
import { useEffect, useState, MouseEvent } from 'react';
import CustodianDetailsViewModel from '../../../Models/custodian/custodian-details-viewmodel';
import CustodianBasicDetails from './CustodianBasicDetails';
import useMainInfoForm from '../../../hooks/Locations/addLocation/useMainInfoForm';
import {
  FormikProps,
  FormikTouched,
  FormikValues,
  setNestedObjectValues,
} from 'formik';
import useAddCustodianLookups from '../../../hooks/Locations/addLocation/useAddCustodianLookups';
import CustodianContactInfo from '../../AddLocation/Components/CustodianContactInfo';
import usePatchCustodian from '../../../hooks/custodians/usePatchCustodian';
import ComponentLoader from '../../../common/ComponentLoader/componentLoader';
import useUI from '../../../hooks/context/useUI';
import useCheckDuplicatePhonesAndEmails from '../../../hooks/Locations/useCheckDuplicatePhonesAndEmails';
import DetailPanel from '../../../components/UI/Panel';
import EntityNotesDetails from './EntityNotesDetails';
import EntityFacilityDetails from './EntityFacilityDetails';
import EntityCustodianDetails from './EntityCustodianDetails';
import { Alert, Box, Button, Typography } from '@mui/material';
import FacilityNotesInfo from '../../AddLocation/Components/FacilityNotesInfo';
import FacilityType from '../../AddLocation/Components/FacilityType';
import CustodianDetails from '../../AddLocation/Components/CustodianDetails';
import { creationDateContainer } from '../Styles/CustodianDetailsHeaderStyles';
import MailingAddress from '../../AddLocation/Components/MailingAddress';
import MailingAddressDetails from './MailingAddressDetails';
import { createFormValues } from '../../../utils/createFormValues';
import mapEntityFormDataToEntity from '../../../utils/mapEntityFormDataToEntity';
import ConfirmationDialog from '../../../components/UI/ConfirmationDialog';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/context/useAuth';
import { appActions } from '../../../settings/appConfig';
import WarningIcon from '@mui/icons-material/Warning';
import { handleDynamicValidation } from '../../../utils/entitiesValidationHelper';
import useUpdateEntityStatus from '../../../hooks/Locations/useUpdateEntityStatus';
import MappedIds from '../../AddLocation/Components/MappedIds';
import EntityMappedIdDetails from './EntityMappedIdDetails';
import { copyAddressFields } from '../../../utils/formUtils';
import styleClasses from '../../../assets/styles/detailPanelStyles'

interface MainInformationTabProps {
  custodian: CustodianDetailsViewModel;
  onCustodianUpdate: any;
  isVisibleEdit: boolean
  setIsVisibleEdit: (val: boolean) => void
}

export default function MainInformationTab(props: Readonly<MainInformationTabProps>) {
  const { custodian, onCustodianUpdate, isVisibleEdit, setIsVisibleEdit } = props;
  const [entityData, setEntityData] = useState(custodian);
  const [isLoading, setIsLoading] = useState(false);
  const [isFacility, setIsFacility] = useState(false);
  const [isCustodian, setIsCustodian] = useState(false);
  const [isConfirmationOpened, setIsConfirmationOpened] = useState(false);
  const { isAuthorized } = useAuth();
  const navigate = useNavigate();

  const { showSnackbar } = useUI();
  const { hasDuplicatePhoneOrEmailError } = useCheckDuplicatePhonesAndEmails();
  const { serveMethods, paymentMethods, departmentFileTypes, states, custodianTypes, phoneTypes,
    recordTypes, portalTypes, timeZones, days, supportedOrderTypes, followUpMethods, sourceTypes } =
    useAddCustodianLookups();
  const patchCustodianBasicInfo = usePatchCustodian().patchCustodian;
  const updateEntityStatus = useUpdateEntityStatus().updateEntityStatus;
  const mainInfoForm: FormikProps<any> = useMainInfoForm({
    phoneTypes: phoneTypes,
    portalTypes: portalTypes,
    recordTypes: recordTypes,
    sourceTypes: sourceTypes,
    custodianTypes: custodianTypes,
    states: states,
  }).mainInfoForm;

  const updateFormFromCustodian = () => {
    (mainInfoForm).resetForm({
      values: createFormValues(custodian),
    });
    setEntityData(custodian);
    return mainInfoForm;
  };

  useEffect(() => {
    updateFormFromCustodian();
  }, [custodian]);

  const patchCustodian = () => {
    const newEntityDataVal = mapEntityFormDataToEntity(mainInfoForm, entityData);
    return patchCustodianBasicInfo(newEntityDataVal);
  };

  const [basicDetailsEditMode, setBasicDetailsEditMode] =
    useState<boolean>(false);

  useEffect(() => {
    if (
      basicDetailsEditMode
    ) {
      // marking all fields as touched
      mainInfoForm.validateForm().then((errors) => {
        if (Object.keys(errors).length !== 0) {
          mainInfoForm.setTouched(
            setNestedObjectValues<FormikTouched<FormikValues>>(errors, true),
          );
        }
      });
    }
  }, [basicDetailsEditMode]);

  const editClickHandler = (
    event: MouseEvent<HTMLDivElement>,
  ): void => {
    event.stopPropagation();
    mainInfoForm.resetForm({ values: createFormValues(entityData) });
    setBasicDetailsEditMode(true);

  };

  const isValid = async () => {
    mainInfoForm.submitForm();
    let mainFormErrors = await mainInfoForm.validateForm(mainInfoForm.values);
    return (
      Object.keys(mainFormErrors).length === 0 &&
      !hasDuplicatePhoneOrEmailError(mainInfoForm)
    );
  };

  const saveClickHandler = async (
    event: MouseEvent<HTMLDivElement>
  ) => {
    let isValidForm = await isValid();
    let isDynamicFormValid = handleDynamicValidation(
      mainInfoForm.values,
      mainInfoForm,
    );
    event.stopPropagation();
    if (isDynamicFormValid && isValidForm) {
      setIsLoading(true);
      patchCustodian().then((res) => {
        if (res !== null) {
          populateLookupData(res);
          setEntityData(res);
          onCustodianUpdate(res);
          setTimeout(() => {
            navigate(0);
          }, 2000);
          exitEditMode();
        }
        setIsLoading(false);
      });
    }
    else {
      showSnackbar(['Please fix validation errors'], 'error');
    }
  };

  const populateLookupData = (custodian: CustodianDetailsViewModel) => {
    if (custodian.address) {
      custodian.address.state =
        states.find((x) => x.id === custodian.address?.stateId)?.name ?? '';
      custodian.address.timeZone =
        timeZones.find((x) => x.id === custodian.address?.timeZoneId)?.name ??
        '';
    }
    if (custodian.phoneNumbers) {
      custodian.phoneNumbers.forEach(
        (phone) =>
        (phone.phoneType =
          phoneTypes.find((x) => x.id === phone.phoneTypeId)?.name ?? ''),
      );
    }
  };

  const exitEditMode = () => {
    mainInfoForm.setValues(mainInfoForm.initialValues);
    setBasicDetailsEditMode(false);
    setIsVisibleEdit(true);
  };

  const entityType = () => {
    if (entityData.isCustodian && entityData.isFacility) {
      return 'both';
    }
    else if (entityData.isCustodian) {
      return 'custodian'
    }
    else if (entityData.isFacility) {
      return 'facility'
    }
  }

  const handleDisableOrEnable = async (isDisabled: boolean) => {

    setIsLoading(true);
    updateEntityStatus(entityData.id, isDisabled).then((res) => {
      if (res !== null) {
        populateLookupData(res);
        setEntityData(res);
        onCustodianUpdate(res);
        exitEditMode();
      }
      setIsLoading(false);
    });

    setIsConfirmationOpened(false)
  }

  const disableConfirmationDescription = () => {
    return (
      entityData.facilityCount > 0 || entityData.custodianCount > 0 ?
        (<>
          This entity has associations mapped to {entityData.name}. Are you sure you want to disable it?
          <br />
          {isAuthorized(appActions.editAssociations) ? (
            <>
              If you wish to manage the associations, please navigate to{' '}
              <Link to={`/disable/${entityData.id}&entityType=${entityType()}`} style={{ color: '#f5831f' }}>
                Manage Associations
              </Link>
            </>
          ) : (
            <>
              <br />
              <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Please note, you do not have the necessary permissions to manage associations.
                </Typography>
              </Alert>
            </>
          )}
        </>) : (
          <>Are you sure you want to disable this entity?</>
        )
    )
  }

  const isObjectEmpty = (obj?: any): boolean => {
    if (obj === null) {
      return false;
    }
    return Object.values(obj).every((value) => value === null || value === '' || value === undefined);
  };

  const copyCustodianAddressDetails = (formKeyName: string) => {
    copyAddressFields(
      mainInfoForm.setFieldValue,
      formKeyName,
      mainInfoForm.values.addressInfo,
    );
  };

  return (
    <ComponentLoader isOpen={isLoading}>
      <Box
        sx={{
          ...creationDateContainer,
          width: "100%",
        }}
        my={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box></Box>
        <Box display="flex" gap={1}>
          {isAuthorized(appActions.editEntity) && (!isVisibleEdit || !basicDetailsEditMode) && <Button
            variant="contained"
            size="small"
            sx={{ minWidth: 120, color: 'white', fontSize: '16px' }}
            onClick={(e: any) => { editClickHandler(e); }}
            disabled={entityData?.disabled}
          >
            Edit
          </Button>}
          {isAuthorized(appActions.editEntity) && <Button
            variant="outlined"
            size="small"
            sx={{ minWidth: 120, fontSize: '16px' }}
            onClick={entityData?.disabled ?
              () => handleDisableOrEnable(false) :
              () => setIsConfirmationOpened(true)}
          >
            {entityData?.disabled ? 'Enable' : 'Disable'}
          </Button>}
        </Box>
      </Box>

      <DetailPanel headerText="Basic Details" styleClasses={styleClasses}>
        {!basicDetailsEditMode && (
          <CustodianBasicDetails custodian={entityData} />
        )}
        {basicDetailsEditMode && (
          <CustodianBasicDetailsEditForm
            formik={mainInfoForm}
            custodianTypes={custodianTypes}
            phoneTypes={phoneTypes}
            portalTypes={portalTypes}
            showPanel={false}
            isFacility={mainInfoForm.values.isFacility}
            setIsFacility={setIsFacility}
            isCustodian={mainInfoForm.values.isCustodian}
            setIsCustodian={setIsCustodian}
            timeZones={timeZones}
            states={states}
            facilityAssociationCount={entityData.facilityCount}
            custodianAssociationCount={entityData.custodianCount} />
        )}
      </DetailPanel>
      {mainInfoForm.values.isCustodian && <DetailPanel headerText="Mailing Details" styleClasses={styleClasses}>
        {!basicDetailsEditMode && (
          <MailingAddressDetails
            mailingDetails={isObjectEmpty(entityData.mailingAddress) ? null : entityData.mailingAddress}
          ></MailingAddressDetails>
        )}
        {basicDetailsEditMode && (
          <MailingAddress
            states={states}
            timeZones={timeZones}
            formik={mainInfoForm}
            copyMainAddress={copyCustodianAddressDetails}
            showPanel={false}
            isEdit={true} />
        )}
      </DetailPanel>}
      {<DetailPanel headerText="Contact Details" styleClasses={styleClasses}>
        {!basicDetailsEditMode && (
          <CustodianContactsDetails
            contacts={entityData?.contacts ?? []} />
        )}
        {basicDetailsEditMode && (
          <CustodianContactInfo formik={mainInfoForm} showPanel={false} />
        )}
      </DetailPanel>}
      {<DetailPanel headerText="Source Ids" styleClasses={styleClasses}>
        {!basicDetailsEditMode && (
          <EntityMappedIdDetails
            externalSourceMappings={entityData?.externalSourceMappings ?? []} />
        )}
        {basicDetailsEditMode && (
          <MappedIds formik={mainInfoForm} sourceTypes={sourceTypes} />
        )}
      </DetailPanel>}
      <DetailPanel headerText="Notes" styleClasses={styleClasses}>
        {!basicDetailsEditMode && (
          <EntityNotesDetails notes={entityData?.notes ?? null} />
        )}
        {basicDetailsEditMode && (
          <FacilityNotesInfo
            formik={mainInfoForm}
            showPanel={false}
          />
        )}
      </DetailPanel>
      {mainInfoForm?.values?.isFacility && (
        <DetailPanel headerText="Facility Details" styleClasses={styleClasses}>
          {!basicDetailsEditMode && (
            <EntityFacilityDetails formik={entityData} facilityDetails={entityData?.facilityTypes ?? null} />
          )}
          {basicDetailsEditMode && (
            <FacilityType
              formik={mainInfoForm}
              showPanel={false}
              isEdit={true}
              custodianTypes={custodianTypes} />
          )}
        </DetailPanel>
      )}
      {mainInfoForm?.values?.isCustodian && (
        <DetailPanel headerText="Custodian Details" styleClasses={styleClasses}>
          {!basicDetailsEditMode && (
            <EntityCustodianDetails custodianDetails={entityData?.custodianDetails ?? null} />
          )}
          {basicDetailsEditMode && (
            <CustodianDetails
              formik={mainInfoForm}
              showPanel={false}
              recordTypes={recordTypes}
              serveMethods={serveMethods}
              states={states}
              timeZones={timeZones}
              days={days}
              paymentMethods={paymentMethods}
              departmentFileTypes={departmentFileTypes}
              orderTypes={supportedOrderTypes}
              followUpMethods={followUpMethods}
              isEdit={true}
            />
          )}
        </DetailPanel>
      )}

      {basicDetailsEditMode && (
        <Box
          display="flex"
          justifyContent="flex-end"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            padding: 1,
            boxShadow: 2,
            zIndex: 1000,
            borderTop: '1px solid #ddd',
          }}
        >
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              sx={{ minWidth: 120, fontSize: '16px' }}
              onClick={(e: any) => { exitEditMode(); }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ minWidth: 120, color: 'white', fontSize: '16px' }}
              onClick={(e: any) => { saveClickHandler(e); }}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {isConfirmationOpened &&
        <ConfirmationDialog
          open={isConfirmationOpened}
          onClose={() => setIsConfirmationOpened(false)}
          title='Confirmation'
          description={disableConfirmationDescription()}
          primaryButtonText='Disable'
          onPrimaryAction={() => handleDisableOrEnable(true)}
          secondaryButtonText='Cancel'
          onSecondaryAction={() => setIsConfirmationOpened(false)}
        />
      }
    </ComponentLoader>
  );
}
